/** @jsxImportSource @emotion/react */

import { useEffect, useState } from 'react';

import useTranslate from '@common/hooks/useTranslate';
import Button from '@stories/atoms/Button';
import { CheckboxWithCounter } from '@stories/atoms/FormElements/Checkbox/Checkbox';
import { isValidOption } from '@stories/templates/Search/Filters/Filters';
import { useProductListContext } from '@utils/productList';

import type { TFilterOption, TFilterSection } from '@stories/@types/custom';

const defaultMaxShownOptions = 8;
const defaultBuffer = 3;

type SectionCheckboxesProps = {
  filterSection: TFilterSection;
  maxShownOptions?: number;
};

const translations = {
  'show all': {
    de_DE: 'Alle anzeigen (${count})'
  },
  'show less': {
    de_DE: 'Weniger anzeigen'
  }
};

const SectionCheckboxes: React.FC<SectionCheckboxesProps> = ({
  filterSection,
  maxShownOptions = defaultMaxShownOptions
}) => {
  const translate = useTranslate(translations);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const [shownOptions, setShownOptions] = useState<TFilterOption[]>([]);
  const { options, name: sectionName } = filterSection;
  const filterSectionStringified = JSON.stringify(filterSection);
  const {
    activeFiltersAndSorting: { activeFilters },
    reducerFunctions: { checkFilter, uncheckFilter }
  } = useProductListContext();

  useEffect(() => {
    const optionsWithCheckedState = options.filter(isValidOption).map((option) => ({
      ...option,
      checked: (activeFilters[sectionName] || []).includes(option.value)
    }));

    if (showAllOptions || optionsWithCheckedState.length < maxShownOptions + defaultBuffer) {
      setShownOptions(optionsWithCheckedState);
    } else {
      const sortedOptions = optionsWithCheckedState;
      setShownOptions(sortedOptions);
    }
  }, [activeFilters, filterSectionStringified, maxShownOptions, options, sectionName, showAllOptions]);

  const onChange = ({ target }) => {
    (target.checked ? checkFilter : uncheckFilter)({
      section: target.name,
      option: target.value
    });
  };

  if (shownOptions.length === 0) {
    return null;
  }

  return (
    <div>
      <div>
        {shownOptions.slice(0, maxShownOptions).map((option, index) => (
          <CheckboxWithCounter
            cssProps={{
              display: 'block',
              margin: '0.25rem 0'
            }}
            hidden={index > 9}
            option={option}
            checked={option.checked}
            onChange={onChange}
            key={option.value}
            name={sectionName}
          />
        ))}
      </div>
      <div
        css={{
          ...(!showAllOptions && { display: 'none' })
        }}
      >
        {shownOptions.slice(maxShownOptions, shownOptions.length).map((option, index) => (
          <CheckboxWithCounter
            cssProps={{
              display: 'block',
              margin: '0.25rem 0'
            }}
            hidden={index > 9}
            option={option}
            checked={option.checked}
            onChange={onChange}
            key={option.value}
            name={sectionName}
          />
        ))}
      </div>
      {shownOptions.length > maxShownOptions + defaultBuffer && (
        <Button
          attrs={{ onClick: () => setShowAllOptions(!showAllOptions) }}
          cssProps={{ marginTop: '0.75rem' }}
          buttonStyle="secondary"
          label={
            showAllOptions
              ? translate('show less')
              : translate('show all', { count: shownOptions.length - maxShownOptions })
          }
          outline
          size="small"
        />
      )}
    </div>
  );
};

export default SectionCheckboxes;
